import { useTranslation } from "react-i18next";
import { useNow } from "@/lib/hooks/use-now";
import { getShiftTiming, splitMinutes } from "@/lib/shift/shift-timing";
import { formatInTz, formatTime } from "@/utils/format-datetime";

type ShiftTimingBadgeProps = {
  startAt: string;
  endAt: string;
  /** Tijdzone van het project. Standaard Europe/Amsterdam. */
  timeZone?: string | null;
};

/**
 * The one line that makes the Vandaag hero worth opening: how long until you have to be somewhere.
 * Falls back to a plain date once the shift is far enough out that a countdown stops being useful.
 */
export function ShiftTimingBadge({ startAt, endAt, timeZone }: ShiftTimingBadgeProps) {
  const { t } = useTranslation();
  const now = useNow();
  const timing = getShiftTiming(startAt, endAt, now);

  const label = (() => {
    switch (timing.state) {
      case "running":
        return t("routes:today.timing.running");
      case "soon": {
        const { hours } = splitMinutes(timing.minutesUntilStart);
        // Under an hour we count minutes. From an hour up we round *down*, so the badge
        // never suggests you have more time than you do.
        return hours < 1
          ? t("routes:today.timing.in-minutes", { count: timing.minutesUntilStart })
          : t("routes:today.timing.in-hours", { count: hours });
      }
      case "today":
        return t("routes:today.timing.today", { time: formatTime(startAt, { timeZone }) });
      case "tomorrow":
        return t("routes:today.timing.tomorrow", { time: formatTime(startAt, { timeZone }) });
      default:
        return formatInTz(startAt, {
          weekday: "long",
          day: "numeric",
          month: "long",
          timeZone,
        });
    }
  })();

  const tone =
    timing.state === "running"
      ? "bg-success-100/10 text-success-120"
      : timing.state === "soon"
        ? "bg-brand-500/10 text-brand-500"
        : "bg-surface-secondary text-txt-secondary";

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold capitalize leading-[1.4] ${tone}`}
    >
      {label}
    </span>
  );
}
